import { useEffect, useRef, useState } from 'react';
import { Flex, VStack } from '@chakra-ui/react';

import ModifierLabel from './ModifierLabel';
import OptionCheckBox from './OptionCheckBox';

import * as fp from 'lodash/fp';

import { CartModifier, CartOption } from '~/types/cart';
import { ModifierWithRestInventoryOption } from '~/types/store/modifier';

import { PLATFORM_ID } from '~/globalVariables';

import { ScrollPositionInfo } from '~/hooks/useScroll';

type MultiModifierGroupProps = {
  modifier: ModifierWithRestInventoryOption;
  cartModifiersMap: Map<string, CartModifier>;
  modifiersErrors: Set<string>;
  itemCount: number;
  positionInfo: ScrollPositionInfo;
  errorModifierId: string;
  onChangeModifier: (modifiers: Map<string, CartModifier>) => void;
};

export default function MultiModifierGroup({
  modifier,
  cartModifiersMap,
  itemCount,
  modifiersErrors,
  positionInfo,
  errorModifierId,
  onChangeModifier,
}: MultiModifierGroupProps) {
  const cartOptions = cartModifiersMap.get(modifier.id)?.options || [];
  const cartOptionsSet = new Set(cartOptions.map(({ id }) => id));

  const [isFullWidth, setIsFullWidth] = useState<boolean>(false);
  const modifierRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (modifierRef.current) {
      modifierRef.current.getBoundingClientRect().top < 160
        ? setIsFullWidth(true)
        : setIsFullWidth(false);
    }
  }, [positionInfo]);

  useEffect(() => {
    if (modifierRef.current && fp.eq(modifier.id, errorModifierId)) {
      const position = modifierRef.current.getBoundingClientRect();
      const htmlEle = document.querySelector('html');
      const bodyPosition = htmlEle?.scrollTop || 0;
      const screenHeight = htmlEle?.clientHeight || 0;
      window.scroll({
        top: bodyPosition + position.top - screenHeight / 2,
      });
    }
  }, [modifier.id, errorModifierId]);

  const handleChangeOption = (option: CartOption) => {
    const options = cartOptions.some(({ id }) => fp.eq(option.id, id))
      ? cartOptions.filter(({ id }) => !fp.eq(option.id, id))
      : [...cartOptions, option];

    const newModifiersMap = new Map(cartModifiersMap);

    newModifiersMap.set(modifier.id, {
      id: modifier.id,
      name: modifier.name,
      options: options.slice(Math.max(options.length - modifier.max, 0)),
    });

    onChangeModifier(newModifiersMap);
  };

  return (
    <Flex direction="column" ref={modifierRef}>
      <ModifierLabel
        isFullWidth={isFullWidth}
        isError={modifiersErrors.has(modifier.id)}
        isRequired={modifier.isRequired}
        title={modifier.name}
        description={`選項 (${modifier.min}~${modifier.max}項)`}
      />
      <VStack align="stretch" spacing={0}>
        {modifier.options
          .filter(({ isAvailable }) => isAvailable)
          .map(({ isAvailable, inventory, containers, restInventory, ...cartOption }) => {
            const isChecked = cartOptionsSet.has(cartOption.id);
            return (
              <OptionCheckBox
                key={cartOption.id}
                value={cartOption.id}
                name={cartOption.name}
                price={cartOption.price}
                isChecked={isChecked}
                isInvalid={isChecked && fp.lt(restInventory - itemCount, 0)}
                isDisabled={!isChecked && fp.lt(restInventory - itemCount, 0)}
                isSoldOut={fp.lte(restInventory, 0)}
                onChange={() =>
                  handleChangeOption({
                    ...cartOption,
                    containers: containers[PLATFORM_ID] || [],
                  })
                }
              />
            );
          })}
      </VStack>
    </Flex>
  );
}
